import { T } from '../lib/tokens'
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useCreateMemo } from '../lib/hooks'

export function Topbar({
  subtitle,
  onGemini,
}: {
  subtitle?: string
  onGemini?: () => void
}) {
  const [query, setQuery] = useState('')
  const [memoOpen, setMemoOpen] = useState(false)
  const navigate = useNavigate()

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && query.trim()) {
      navigate({ to: '/search' })
    }
  }

  return (
    <>
      <header style={{
        height: 44, flexShrink: 0,
        borderBottom: `1px solid ${T.border}`,
        background: T.bg,
        display: 'flex', alignItems: 'center',
        gap: 10, padding: '0 16px',
        fontFamily: T.font,
      }}>
        {/* 検索バー */}
        <div style={{
          flex: 1, maxWidth: 480,
          display: 'flex', alignItems: 'center', gap: 8,
          background: T.bgRaised, border: `1px solid ${T.border}`,
          borderRadius: 6, padding: '0 10px', height: 28,
        }}>
          <span style={{ fontSize: 12, color: T.textMute, fontFamily: T.fontMono }}>⌕</span>
          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="検索… (Enter)"
            style={{
              flex: 1, background: 'transparent', border: 'none', outline: 'none',
              color: T.text, fontSize: 12, fontFamily: T.font,
            }}
          />
        </div>

        {/* ＋ メモボタン */}
        <button
          onClick={() => setMemoOpen(true)}
          title="メモを保存（Geminiなし）"
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            height: 28, padding: '0 10px', borderRadius: 6,
            background: 'transparent', border: `1px solid ${T.border}`,
            color: T.textDim, fontSize: 12, cursor: 'pointer', flexShrink: 0,
            fontFamily: T.fontMono,
          }}
        >＋ メモ</button>

        {/* Gemini ボタン */}
        <button
          onClick={onGemini}
          title="Ask the Lake (⌘J)"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 28, height: 28, borderRadius: 6,
            background: T.accentSoft, border: `1px solid ${T.accentBorder}`,
            color: 'oklch(0.85 0.12 258)',
            fontSize: 14, cursor: 'pointer', flexShrink: 0,
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => (e.currentTarget.style.background = 'oklch(0.72 0.16 258 / 0.2)')}
          onMouseLeave={e => (e.currentTarget.style.background = T.accentSoft)}
        >✦</button>

        {/* ステータス */}
        {subtitle && (
          <div style={{
            fontSize: 11, color: T.textMute,
            fontFamily: T.fontMono, display: 'flex', alignItems: 'center', gap: 5,
            flexShrink: 0,
          }}>
            <span style={{ color: T.ok, fontSize: 8 }}>●</span>
            {subtitle}
          </div>
        )}
      </header>

      {memoOpen && <QuickMemoModal onClose={() => setMemoOpen(false)} />}
    </>
  )
}

function QuickMemoModal({ onClose }: { onClose: () => void }) {
  const [text, setText] = useState('')
  const { mutate: createMemo, isPending, isError } = useCreateMemo()

  function submit() {
    if (!text.trim() || isPending) return
    createMemo({ content: text.trim() }, {
      onSuccess: () => {
        setText('')
        onClose()
      },
    })
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 200,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: T.bgRaised, border: `1px solid ${T.borderHi}`,
          borderRadius: 10, padding: 20, width: 480, maxWidth: '90vw',
          display: 'flex', flexDirection: 'column', gap: 12, fontFamily: T.font,
        }}
      >
        <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>✏️ メモを保存（Geminiなし）</div>
        <textarea
          autoFocus
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape') onClose() }}
          placeholder="テキストまたはURLを入力…"
          rows={5}
          style={{
            width: '100%', background: T.bg, border: `1px solid ${T.borderHi}`,
            borderRadius: 6, padding: '10px 12px', color: T.text, fontSize: 13,
            fontFamily: T.font, resize: 'vertical', outline: 'none', boxSizing: 'border-box',
          }}
        />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          {isError && <span style={{ fontSize: 11, color: T.warn, flex: 1 }}>保存に失敗しました</span>}
          <button onClick={onClose} style={{ padding: '6px 14px', borderRadius: 5, background: 'transparent', border: `1px solid ${T.border}`, color: T.textDim, fontSize: 12, cursor: 'pointer', fontFamily: T.fontMono }}>キャンセル</button>
          <button
            onClick={submit}
            disabled={!text.trim() || isPending}
            style={{
              padding: '6px 16px', borderRadius: 5, border: 'none',
              background: text.trim() ? T.accent : T.bgHover,
              color: text.trim() ? '#0a0b0e' : T.textMute,
              fontSize: 12, fontWeight: 600, cursor: text.trim() ? 'pointer' : 'default', fontFamily: T.fontMono,
            }}
          >{isPending ? '保存中…' : '保存'}</button>
        </div>
      </div>
    </div>
  )
}
